<template>
  <div class="container-fluid">
    <div class="header">
      <h1>🤖 Panel Administrativo Bot NOVA</h1>
      <p>Gestión de Preguntas Dinámicas - SIREP Apostar</p>
    </div>
    
    <div class="content">
      <!-- Estado de conexión -->
      <div v-if="loading" class="loading">
        <div>🔄 Conectando con el servidor...</div>
      </div>
      
      <!-- Error de conexión -->
      <div v-if="error" class="alert alert-error">
        <strong>❌ Error de conexión:</strong> {{ error }}
        <br><small>Verifica que el backend esté ejecutándose en: {{ apiUrl }}</small>
      </div>
      
      <!-- Panel principal -->
      <div v-if="!loading && !error">
        <div class="alert alert-success">
          <strong>✅ Conectado exitosamente</strong>
          <br>API: {{ apiUrl }}
        </div>
        
        <!-- 🔍 Buscador de requerimientos -->
        <BuscadorRequerimientos 
          :requerimientos="requerimientos"
          :requerimiento-seleccionado="requerimientoSeleccionado"
          @requerimiento-seleccionado="seleccionarRequerimiento"
        />
        
        <!-- ✅ Requerimiento seleccionado -->
        <RequerimientoSeleccionado 
          :requerimiento-actual="requerimientoActual"
          @limpiar-seleccion="limpiarSeleccion"
        />
        
        <!-- 📋 Lista de preguntas -->
              <ListaPreguntas
                v-if="requerimientoSeleccionado"
                :preguntas="preguntas"
                @crear-pregunta="abrirFormularioNueva"
                @editar-pregunta="editarPregunta"
                @confirmar-eliminar="confirmarEliminar"
              />
      </div>
    </div>
    
    <!-- 📝 Modal Formulario Pregunta -->
    <FormularioPregunta
      :mostrar="mostrarFormulario"
      :es-edicion="esEdicion"
      :pregunta-editando="preguntaEditando"
      :guardando="guardandoPregunta"
      @guardar-pregunta="guardarPregunta"
      @cerrar-formulario="cerrarFormulario"
    />
    
    <!-- 🗑️ Modal Confirmación Eliminar -->
    <ModalConfirmacion
      :mostrar="mostrarConfirmacion"
      :pregunta="preguntaAEliminar"
      :eliminando="eliminandoPregunta"
      @confirmar-eliminacion="eliminarPregunta"
      @cancelar-eliminacion="cancelarEliminar"
    />
    
    <!-- 🔧 Información del sistema - Al final -->
    <div v-if="!loading && !error" class="info-sistema">
      <div class="info-container">
        <h4>🔧 Información del Sistema</h4>
        <div class="info-content">
          <div><strong>Funcionalidades:</strong> ✅ Gestión de preguntas, ✅ Vista previa, ✅ Validación</div>
          <div><strong>API Backend:</strong> {{ apiUrl }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import env from '../../../env'
import BuscadorRequerimientos from './BuscadorRequerimientos.vue'
import RequerimientoSeleccionado from './RequerimientoSeleccionado.vue'
import ListaPreguntas from './ListaPreguntas.vue'
import FormularioPregunta from './FormularioPregunta.vue'
import ModalConfirmacion from './ModalConfirmacion.vue'

export default {
  name: 'PanelAdminBot',
  components: {
    BuscadorRequerimientos,
    RequerimientoSeleccionado,
    ListaPreguntas,
    FormularioPregunta,
    ModalConfirmacion
  },
  data() {
    return {
      // Configuración
      apiUrl: env.endpoint + 'callcenter/',
      
      // Estado
      loading: true,
      error: null,
      
      // Datos
      requerimientos: [],
      preguntas: [],
      requerimientoSeleccionado: '',
      requerimientoActual: null,
      
      // Formulario
      mostrarFormulario: false,
      esEdicion: false,
      guardandoPregunta: false,
      preguntaEditando: null,
      
      // Eliminación
      mostrarConfirmacion: false,
      preguntaAEliminar: null,
      eliminandoPregunta: false
    }
  },
  
  async mounted() {
    // 🆕 Verificar acceso autorizado
    this.verificarAccesoAutorizado();
    
    await this.cargarRequerimientos();
  },
  
  methods: {
    // 🔒 VERIFICAR ACCESO AUTORIZADO
    verificarAccesoAutorizado() {
      const idRaw = localStorage.getItem('id');
      const idUsuario = parseInt(idRaw, 10);
      const idUsuarioAutorizado = 1;
      
      if (isNaN(idUsuario) || idUsuario !== idUsuarioAutorizado) {
        console.warn('🚫 Acceso no autorizado al Panel Admin Bot');
        const mensaje = isNaN(idUsuario) ? 
          'Error: No se pudo obtener el ID del usuario. Debes iniciar sesión nuevamente.' : 
          'Acceso Denegado: Solo el usuario autorizado puede acceder a esta sección';
        
        this.mostrarNotificacion(mensaje, 'error');
        
        // Redirigir después de 2 segundos
        setTimeout(() => {
          if (isNaN(idUsuario)) {
            this.$router.push('/'); // Redirigir al login si no hay ID válido
          } else {
            this.$router.push('/user'); // Redirigir a user si no tiene permisos
          }
        }, 2000);
        
        return false;
      }
      
      return true;
    },
    
    // 📡 CARGAR DATOS
    async cargarRequerimientos() {
  try {
    this.loading = true;
    this.error = null;
    
    const idUsuario = localStorage.getItem('id');
    const url = `${this.apiUrl}requerimientostodos?idUsuario=${idUsuario}&todos=true`;
    
    console.log('🌐 Llamando endpoint:', url);
    
    const response = await fetch(url);
    
    // Leer como texto primero para debug
    const responseText = await response.text();
    console.log('📄 Respuesta del servidor (primeros 200 chars):', responseText.substring(0, 200));
    
    // Intentar parsear JSON
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (jsonError) {
      console.error('❌ Error parseando JSON:', jsonError);
      throw new Error('El servidor no devolvió JSON válido');
    }
    
    // Procesar respuesta
    if (response.ok) {
      if (Array.isArray(data)) {
        this.requerimientos = data;
        console.log('✅ Requerimientos cargados:', this.requerimientos.length);
        
        // DEBUG: Mostrar estructura del primer elemento
        if (this.requerimientos.length > 0) {
          console.log('📋 Estructura del primer requerimiento:', this.requerimientos[0]);
          console.log('🔑 Campos disponibles:', Object.keys(this.requerimientos[0]));
        }
      } else if (data && data.estado !== false && Array.isArray(data.requerimientos)) {
        this.requerimientos = data.requerimientos;
      } else if (data && data.estado === false) {
        throw new Error(data.mensaje || 'Error del servidor');
      } else {
        this.requerimientos = [];
      }
    } else {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
  } catch (err) {
    console.error('❌ Error cargando requerimientos:', err);
    this.error = err.message;
    this.requerimientos = [];
  } finally {
    this.loading = false;
  }
},

async cargarPreguntas(forzar = false) {
  if (!this.requerimientoSeleccionado && !forzar) {
    this.preguntas = [];
    return;
  }
  
  try {
    console.log("🚨 ========== CARGAR PREGUNTAS ==========");
    console.log("📋 requerimientoSeleccionado:", this.requerimientoSeleccionado);
    console.log("📋 requerimientoActual:", this.requerimientoActual);
    
    // 🔥 DETERMINAR idEmpresa CORRECTAMENTE
    let idEmpresa = 2; // Default a Apostar (más seguro)
    let idReq = null;
    
    // OPCIÓN 1: Si tenemos el objeto completo
    if (this.requerimientoActual && typeof this.requerimientoActual === 'object') {
      idReq = this.requerimientoActual.cc_id_requerimientos;
      
      // 🔥 CRÍTICO: Usar nombreEmpresa para determinar idEmpresa
      if (this.requerimientoActual.nombreEmpresa) {
        if (this.requerimientoActual.nombreEmpresa.toLowerCase().includes('facilisimo')) {
          idEmpresa = 1;
          console.log("✅ Determinado por nombreEmpresa: Facilisimo -> idEmpresa=1");
        } else {
          idEmpresa = 2;
          console.log("✅ Determinado por nombreEmpresa: Apostar -> idEmpresa=2");
        }
      }
      // 🔥 Si no hay nombreEmpresa, usar idEmpresa del objeto
      else if (this.requerimientoActual.idEmpresa === 1 || this.requerimientoActual.idEmpresa === 2) {
        idEmpresa = this.requerimientoActual.idEmpresa;
        console.log("✅ Usando idEmpresa del objeto:", idEmpresa);
      }
      // 🔥 Si no hay nada, inferir por nombre o área
      else {
        const nombre = (this.requerimientoActual.cc_nombre_requerimiento || '').toLowerCase();
        const area = (this.requerimientoActual.cc_area_interna || this.requerimientoActual.cc_nombre_area || '').toLowerCase();
        
        // Heurística: Si parece de Facilisimo
        if (nombre.includes('teclado') || nombre.includes('mouse') || nombre.includes('silla') || 
            nombre.includes('monitor') || nombre.includes('impresora') || area.includes('sistemas')) {
          idEmpresa = 1;
          console.log("✅ Inferido por heurística: Facilisimo -> idEmpresa=1");
        } else {
          idEmpresa = 2;
          console.log("✅ Inferido por heurística: Apostar -> idEmpresa=2");
        }
      }
    }
    // OPCIÓN 2: Si es string con formato
    else if (typeof this.requerimientoSeleccionado === 'string' && this.requerimientoSeleccionado.includes('-')) {
      console.log("🔍 Parseando formato con guión");
      const partes = this.requerimientoSeleccionado.split('-');
      idReq = partes[1];
      
      // Determinar empresa del prefijo
      const prefijo = partes[0].toLowerCase();
      if (prefijo.includes('facilisimo') || prefijo === '1' || prefijo === 'f') {
        idEmpresa = 1;
        console.log("✅ Parseado de prefijo: Facilisimo -> idEmpresa=1");
      } else if (prefijo.includes('apostar') || prefijo === '2' || prefijo === 'a') {
        idEmpresa = 2;
        console.log("✅ Parseado de prefijo: Apostar -> idEmpresa=2");
      } else {
        idEmpresa = parseInt(prefijo) || 2;
        console.log("⚠️ Prefijo numérico:", idEmpresa);
      }
    }
    // OPCIÓN 3: Solo ID
    else {
      idReq = this.requerimientoSeleccionado;
      console.warn("⚠️ Solo ID disponible, usando Apostar por defecto");
    }
    
    console.log('🎯 Parámetros finales:');
    console.log('   - idRequerimiento:', idReq);
    console.log('   - idEmpresa:', idEmpresa, '(', idEmpresa === 1 ? 'Facilisimo' : 'Apostar', ')');
    
    const timestamp = new Date().getTime();
    const url = `${this.apiUrl}preguntas_bot?idRequerimiento=${idReq}&idEmpresa=${idEmpresa}&_=${timestamp}`;
    
    console.log('📡 URL:', url);
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (data.estado !== false) {
      const preguntasOrdenadas = (data.preguntas || data || [])
        .sort((a, b) => (a.orden || 0) - (b.orden || 0));
      
      // 🔥 AGREGAR idEmpresa a cada pregunta para mostrarla en UI
      this.preguntas = preguntasOrdenadas.map(p => ({
        ...p,
        idEmpresa: idEmpresa, // 🔥 Añadir idEmpresa a cada pregunta
        empresaNombre: idEmpresa === 1 ? 'Facilisimo' : 'Apostar' // 🔥 Nombre para mostrar
      }));
      
      console.log(`✅ ${this.preguntas.length} preguntas cargadas`);
      console.log(`   - Empresa: ${idEmpresa === 1 ? 'Facilisimo' : 'Apostar'}`);
      
    } else {
      this.preguntas = [];
      console.warn('⚠️ Servidor reportó error:', data.mensaje);
    }
    
  } catch (err) {
    console.error('💥 Error cargando preguntas:', err);
    this.preguntas = [];
  }
},
    
    // 🎯 GESTIÓN DE REQUERIMIENTOS
    seleccionarRequerimiento(requerimiento) {
      // 🆕 Crear identificador único combinando Empresa + ID
      const uniqueId = (requerimiento.idEmpresa || requerimiento.nombreEmpresa) + '-' + requerimiento.cc_id_requerimientos;
      this.requerimientoSeleccionado = uniqueId;
      this.requerimientoActual = requerimiento;
      this.cargarPreguntas();
    },
    
    limpiarSeleccion() {
      this.requerimientoSeleccionado = '';
      this.requerimientoActual = null;
      this.preguntas = [];
    },
    
    // 📝 GESTIÓN DE PREGUNTAS
    abrirFormularioNueva() {
      this.esEdicion = false;
      this.preguntaEditando = null;
      this.mostrarFormulario = true;
    },
    
    editarPregunta(pregunta) {
      this.esEdicion = true;
      this.preguntaEditando = pregunta;
      this.mostrarFormulario = true;
    },
    
    // 💾 GUARDAR PREGUNTA
async guardarPregunta(datosFormulario) {
  try {
    this.guardandoPregunta = true;
    
    console.log('📤 GUARDAR PREGUNTA - Inicio');
    console.log('📝 Datos del formulario:', datosFormulario);
    console.log('🎯 Requerimiento seleccionado:', this.requerimientoSeleccionado);
    console.log('🏢 Requerimiento actual:', this.requerimientoActual);
    
    // 🎯 EXTRAER idEmpresa E idRequerimiento CORRECTAMENTE
    let idEmpresa = null;
    let idRequerimiento = null;
    
    // CASO 1: Si tenemos el objeto requerimientoActual
    if (this.requerimientoActual) {
      idEmpresa = this.requerimientoActual.idEmpresa || 
                  (this.requerimientoActual.nombreEmpresa === 'Facilisimo' ? 1 : 2);
      idRequerimiento = this.requerimientoActual.cc_id_requerimientos;
      console.log('✅ Usando requerimientoActual:', { idEmpresa, idRequerimiento });
    }
    // CASO 2: Si tenemos el string requerimientoSeleccionado
    else if (this.requerimientoSeleccionado && this.requerimientoSeleccionado.includes('-')) {
      const partes = this.requerimientoSeleccionado.split('-');
      
      // Manejar "Facilisimo-72" o "1-72" o "Apostar-148" o "2-148"
      if (partes[0] === 'Facilisimo' || partes[0] === '1') {
        idEmpresa = 1;
      } else if (partes[0] === 'Apostar' || partes[0] === '2') {
        idEmpresa = 2;
      } else {
        idEmpresa = parseInt(partes[0]) || 1;
      }
      
      idRequerimiento = parseInt(partes[1]) || partes[1];
      console.log('✅ Usando requerimientoSeleccionado:', { idEmpresa, idRequerimiento });
    }
    // CASO 3: Fallback
    else {
      idEmpresa = 1; // Default a Facilisimo
      idRequerimiento = this.requerimientoSeleccionado || datosFormulario.idRequerimiento;
      console.warn('⚠️ Usando fallback:', { idEmpresa, idRequerimiento });
    }
    
    // 🎯 VALIDACIÓN CRÍTICA
    if (!idRequerimiento || idRequerimiento === 'undefined' || idRequerimiento === 'null') {
      throw new Error('No se pudo determinar el ID del requerimiento');
    }
    
    if (!idEmpresa || (idEmpresa !== 1 && idEmpresa !== 2)) {
      throw new Error('Empresa inválida. Debe ser 1 (Facilisimo) o 2 (Apostar)');
    }
    
    console.log('🎯 Datos finales para enviar:', {
      idEmpresa,
      idRequerimiento,
      empresa: idEmpresa === 1 ? 'Facilisimo' : 'Apostar'
    });
    
    // 🎯 PREPARAR DATOS PARA ENVIAR
    const datos = new URLSearchParams();
    
    // 🎯 DATOS OBLIGATORIOS
    datos.append('idEmpresa', idEmpresa);
    datos.append('idRequerimiento', idRequerimiento);
    
    // 🎯 Si es edición, enviar idPregunta
    if (this.esEdicion && datosFormulario.id) {
      datos.append('idPregunta', datosFormulario.id);
      console.log('✏️ Editando pregunta ID:', datosFormulario.id);
    } else if (!this.esEdicion) {
      // Si es creación, no enviar idPregunta
      console.log('➕ Creando nueva pregunta');
    }
    
    // 🎯 CAMPOS DEL FORMULARIO
    datos.append('orden', datosFormulario.orden || 1);
    datos.append('pregunta', datosFormulario.pregunta ? datosFormulario.pregunta.trim() : '');
    datos.append('tipoPregunta', datosFormulario.tipoPregunta || 1);
    datos.append('tipoRespuesta', datosFormulario.tipoRespuesta || 1);
    datos.append('accionSiguiente', datosFormulario.accionSiguiente || 1);
    datos.append('esObligatoria', datosFormulario.esObligatoria ? '1' : '0');
    
    if (datosFormulario.mensajeEscalado) {
      datos.append('mensajeEscalado', datosFormulario.mensajeEscalado.trim());
    }
    
    if (datosFormulario.validacion) {
      datos.append('validacion', datosFormulario.validacion.trim());
    }
    
    // Usuario
    const idUsuario = localStorage.getItem('id') || 1;
    datos.append('idUsuario', idUsuario);
    
    console.log('📤 Datos completos a enviar:', Object.fromEntries(datos));
    
    // 🎯 DETERMINAR ENDPOINT
    const endpoint = this.esEdicion ? 'actualizar_pregunta_bot' : 'crear_pregunta_bot';
    const url = `${this.apiUrl}${endpoint}`;
    
    console.log('📡 Enviando a:', url);
    console.log('🎯 Método:', this.esEdicion ? 'PUT (Actualizar)' : 'POST (Crear)');
    console.log('🏢 Empresa:', idEmpresa === 1 ? 'Facilisimo' : 'Apostar');
    console.log('🔢 ID Requerimiento:', idRequerimiento);
    
    // 🎯 ENVIAR DATOS CON MANEJO DE ERRORES MEJORADO
    const response = await fetch(url, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json'
      },
      body: datos
    });
    
    // 🎯 MANEJAR RESPUESTA
    console.log('📡 Estado HTTP:', response.status, response.statusText);
    
    // Leer como texto primero para debug
    const responseText = await response.text();
    console.log('📦 Respuesta cruda (primeros 500 chars):', responseText.substring(0, 500));
    
    // Intentar parsear como JSON
    let result;
    try {
      result = JSON.parse(responseText);
      console.log('✅ JSON parseado:', result);
    } catch (jsonError) {
      console.error('❌ Error parseando JSON:', jsonError);
      console.error('🔍 Texto que falló:', responseText.substring(0, 300));
      throw new Error('El servidor no devolvió JSON válido. Posible error PHP.');
    }
    
    if (response.ok && result.estado !== false) {
      // 🎯 ÉXITO
      this.cerrarFormulario();
      
      // 🎯 RECARGAR PREGUNTAS
      await this.cargarPreguntas();
      
      // 🎯 MOSTRAR NOTIFICACIÓN
      this.mostrarNotificacion(
        this.esEdicion ? '✅ Pregunta actualizada exitosamente' : '✅ Pregunta creada exitosamente',
        'success'
      );
      
      console.log('🎉 Operación completada exitosamente');
      
    } else {
      // 🎯 ERROR DEL SERVIDOR
      const errorMsg = result.mensaje || `Error ${response.status}: ${response.statusText}`;
      console.error('❌ Error del servidor:', errorMsg, result);
      throw new Error(errorMsg);
    }
    
  } catch (err) {
    console.error('💥 Error en guardarPregunta:', err);
    
    // 🎯 MOSTRAR ERROR DETALLADO
    let errorMessage = err.message;
    
    if (err.message.includes('JSON')) {
      errorMessage = 'Error: El servidor respondió con un formato incorrecto. Verifica los logs del backend.';
    } else if (err.message.includes('NetworkError') || err.message.includes('Failed to fetch')) {
      errorMessage = 'Error de conexión con el servidor. Verifica que el backend esté funcionando.';
    }
    
    this.mostrarNotificacion(`❌ Error: ${errorMessage}`, 'error');
    
  } finally {
    this.guardandoPregunta = false;
  }
},

// Añade este método para debug
debugGuardarPregunta() {
  console.log('🔍 DEBUG - Estado actual:');
  console.log('1. requerimientoSeleccionado:', this.requerimientoSeleccionado);
  console.log('2. requerimientoActual:', this.requerimientoActual);
  console.log('3. empresa detectada:', this.requerimientoActual?.idEmpresa || 'No detectada');
  console.log('4. idRequerimiento detectado:', this.requerimientoActual?.cc_id_requerimientos || 'No detectado');
  console.log('5. nombreEmpresa:', this.requerimientoActual?.nombreEmpresa || 'No detectado');
  
  // Prueba manual
  const testData = {
    id: 1, // Cambia por un ID real
    pregunta: 'Pregunta de prueba',
    orden: 1,
    tipoPregunta: 1,
    tipoRespuesta: 1,
    accionSiguiente: 1,
    esObligatoria: false,
    mensajeEscalado: '',
    validacion: ''
  };
  
  console.log('🧪 Datos de prueba:', testData);
  
  // Simular envío
  this.guardarPregunta(testData);
},

// Llama desde consola: vm.debugGuardarPregunta()

// 🆕 Añade este método para scroll a pregunta específica
scrollToPregunta(idPregunta) {
  // Buscar el elemento en el DOM después de que Vue actualice
  this.$nextTick(() => {
    setTimeout(() => {
      const elemento = document.querySelector(`[data-pregunta-id="${idPregunta}"]`);
      if (elemento) {
        elemento.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Efecto visual de highlight
        elemento.style.backgroundColor = '#e8f5e9';
        elemento.style.transition = 'background-color 0.5s';
        
        setTimeout(() => {
          elemento.style.backgroundColor = '';
        }, 2000);
      }
    }, 300);
  });
},
    
    
    // 🗑️ ELIMINAR PREGUNTA
    confirmarEliminar(pregunta) {
      this.preguntaAEliminar = pregunta;
      this.mostrarConfirmacion = true;
    },
    
    async eliminarPregunta() {
      try {
        this.eliminandoPregunta = true;
        
        // 🔧 GET con parámetro en query string (como especifica el backend)
        const response = await fetch(
          `${this.apiUrl}eliminar_pregunta_bot?idPregunta=${this.preguntaAEliminar.id_pregunta}&idEmpresa=${this.preguntaAEliminar.idEmpresa || ''}`,
          { method: 'GET' }
        );
        
        const result = await response.json();
        
        if (response.ok && result.estado !== false) {
          this.cancelarEliminar();
          await this.cargarPreguntas();
          
          this.mostrarNotificacion('🗑️ Pregunta eliminada exitosamente', 'success');
        } else {
          throw new Error(result.mensaje || 'Error al eliminar la pregunta');
        }
        
      } catch (err) {
        console.error('❌ Error eliminando pregunta:', err);
        this.mostrarNotificacion('❌ Error: ' + err.message, 'error');
      } finally {
        this.eliminandoPregunta = false;
      }
    },
    
    // 🔄 MÉTODOS DE FORMULARIO
    cerrarFormulario() {
      this.mostrarFormulario = false;
      this.esEdicion = false;
      this.preguntaEditando = null;
    },
    
    cancelarEliminar() {
      this.mostrarConfirmacion = false;
      this.preguntaAEliminar = null;
    },
    
    // 🔔 NOTIFICACIONES
    mostrarNotificacion(mensaje, tipo = 'info') {
      const notification = document.createElement('div');
      notification.className = `notification notification-${tipo}`;
      notification.textContent = mensaje;
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        padding: 1rem 1.5rem;
        border-radius: 6px;
        color: white;
        z-index: 10000;
        font-weight: 500;
        background: ${tipo === 'success' ? '#28a745' : tipo === 'error' ? '#dc3545' : '#17a2b8'};
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        animation: slideIn 0.3s ease-out;
      `;
      
      document.body.appendChild(notification);
      
      setTimeout(() => {
        notification.style.animation = 'slideOut 0.3s ease-in';
        setTimeout(() => notification.remove(), 300);
      }, 3000);
    }
  }
}
</script>

<style>
/* 🎨 Estilos principales del contenedor */
.container-fluid {
  max-width: 1200px;
  margin: 0 auto;
  background: linear-gradient(145deg, #ffffff, #f8f9fa);
  border-radius: 20px;
  box-shadow: 
    0 20px 40px rgba(41, 70, 151, 0.1),
    0 8px 16px rgba(0, 0, 0, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  overflow: visible;
  border: 1px solid rgba(41, 70, 151, 0.1);
  position: relative;
  border-radius: 20px;
}


.header {
  background: linear-gradient(135deg, #1D3C7F 0%, #294697 50%, #007bff 100%);
  color: white;
  padding: 1.5rem 0.5rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  border-radius: 20px 20px 0 0;
}

/* 🔧 Efecto shimmer removido - diseño más sobrio */

.header h1 {
  font-size: 2.2rem;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
  margin-bottom: 0.5rem;
}

.header p {
  font-size: 1.1rem;
  opacity: 0.9;
  text-shadow: 0 1px 2px rgba(0,0,0,0.2);
}

.content {
  padding: 1.5rem 2rem;
  background: linear-gradient(145deg, #ffffff, #f8f9fa);
  overflow: visible;
  border-radius: 0 0 20px 20px;
}

/* 🚨 Alertas y estados mejoradas */
.alert {
  padding: 1.5rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  border: none;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  position: relative;
  overflow: hidden;
}

.alert::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: currentColor;
  opacity: 0.3;
}

.alert-success {
  background: linear-gradient(135deg, #d4edda, #c3e6cb);
  color: #155724;
  border-left: 4px solid #28a745;
}

.alert-error {
  background: linear-gradient(135deg, #f8d7da, #f5c6cb);
  color: #721c24;
  border-left: 4px solid #dc3545;
}

.loading {
  text-align: center;
  padding: 3rem 2rem;
  color: #6c757d;
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-radius: 12px;
  border: 2px dashed #dee2e6;
}

.loading div {
  font-size: 1.1rem;
  font-weight: 500;
  animation: pulse 2s ease-in-out infinite;
}

/* 🔔 Notificaciones mejoradas */
@keyframes slideIn {
  from { 
    transform: translateX(100%) scale(0.9); 
    opacity: 0; 
  }
  to { 
    transform: translateX(0) scale(1); 
    opacity: 1; 
  }
}

@keyframes slideOut {
  from { 
    transform: translateX(0) scale(1); 
    opacity: 1; 
  }
  to { 
    transform: translateX(100%) scale(0.9); 
    opacity: 0; 
  }
}

/* 🔧 Animación shimmer removida */

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

/* 🔧 Información del sistema - Estilos */
.info-sistema {
  margin-top: 3rem;
  padding: 0 2rem 1.5rem; 
  display: flex;
  justify-content: center;
}

.info-container {
  padding: 1rem;
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-radius: 12px;
  border: 1px solid rgba(41, 70, 151, 0.1);
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  width: 100%;
  box-shadow: 0 4px 12px rgba(41, 70, 151, 0.05);
}

.info-container h4 {
  color: #294697;
  font-weight: 700;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  text-align: center;
}

.info-content {
  font-size: 0.9rem;
  color: #6c757d;
  line-height: 1.6;
  display: flex;
  flex-direction: column;
  align-items: center;

}

.info-content div {
  margin-bottom: 0.5rem;
}

.info-content strong {
  color: #495057;
  font-weight: 600;
}

/* 📱 Responsive mejorado */
@media (max-width: 768px) {
  .content {
    padding: 1rem 1.25rem; /* compactar */
  }
  
  .header {
    padding: 2rem 1rem;
  }
  
  .header h1 {
    font-size: 1.8rem;
  }
  
  .container-fluid {
    margin: 1rem;
    border-radius: 16px;
    overflow: visible;
  }
  
  .info-sistema {
    padding: 0 1.25rem 1.25rem;
  }
  
  .info-container {
    padding: 1rem;
  }
}

@media (max-width: 480px) {
  .header h1 {
    font-size: 1.5rem;
  }
  
  .header p {
    font-size: 1rem;
  }
  
  .content {
    padding: 1rem;
  }
  
  .info-sistema {
    padding: 0 0.75rem 0.75rem;
  }
  
  .info-container {
    padding: 0.875rem;
  }
}
</style>
